import { zodResolver } from "@hookform/resolvers/zod";

import { Fragment, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "@/api/error";
import { modal } from "@/app/store/modal.store";
import { toast } from "@/app/store/toast.store";
import { useLogin } from "@/shared/hooks/use-login";
import { useTermsList } from "@/shared/hooks/use-terms";

import { loginSchema, type LoginFormValues } from "@/features/auth/schemas/login.schema";

import { LoginForm } from "./LoginForm";
import { ShcLogo } from "@/components/brand/ShcLogo";
import { useInstitution } from "@/features/auth/institution/useInstitution";

export const MedicalLoginPage = () => {
  const { t } = useTranslation(["auth", "common", "validation"]);
  const institution = useInstitution();
  const navigate = useNavigate();
  const location = useLocation();
  const loginMutation = useLogin();
  // Public terms list (permitAll on BE) — footer shows only the required (필수) legal docs.
  const { data: terms } = useTermsList();
  const footerTerms = (terms ?? []).filter((term) => term.required);
  const [isLocked, setIsLocked] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: "onTouched",
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = (data: LoginFormValues) => {
    loginMutation.mutate(data, {
      onSuccess: () => {
        // Honor `state.from` set by ProtectedRoute when the user was bounced
        // here from a protected URL. Fallback to /home for direct logins.
        const from = (location.state as { from?: { pathname: string } } | null)
          ?.from?.pathname;
        navigate(from || "/home", { replace: true });
      },
      onError: (error) => {
        if (!(error instanceof ApiError)) {
          // Non-API failure (network drop, JSON parse error, etc.) — PRD §8.2.
          toast.error(t("common:error.serverUnreachable"));
          return;
        }
        const failureCount = error.meta?.failureCount as number | undefined;
        switch (error.code) {
          case "ACCOUNT_LOCKED":
            setIsLocked(true);
            setError("root", { message: t("validation:auth.accountLocked") });
            break;
          case "ACCOUNT_WITHDRAWN":
            modal.alert(
              t("auth:login.idHelpTitle"),
              t("validation:auth.accountWithdrawn"),
            );
            break;
          case "INACTIVE_ACCOUNT":
            modal.alert(
              t("auth:login.idHelpTitle"),
              t("validation:auth.accountInactive"),
            );
            break;
          case "INVALID_CREDENTIALS":
            setError("root", {
              message:
                failureCount !== undefined
                  ? t("validation:auth.invalidCredentialsWithCount", {
                      count: failureCount,
                    })
                  : t("validation:auth.invalidCredentials"),
            });
            break;
          default:
            // Unknown / INTERNAL_ERROR / 5xx — PRD §8.2 toast.
            toast.error(t("common:error.serverUnreachable"));
        }
      },
    });
  };

  const handleShowIdHelp = () => {
    modal.alert(
      t("auth:login.idHelpTitle"),
      // The organization name comes from the same fetched row the footer uses (V129), so the two places
      // this screen names the organization can no longer disagree with each other or with the backend.
      <Trans
        i18nKey="login.idHelpBody"
        ns="auth"
        values={{ institution: institution.name ?? "" }}
        components={{ strong: <strong /> }}
      />,
    );
  };

  return (
    <main className="login-page login-page--medical">
      {/* Container — matches prototype login-page__container (440px via CSS) */}
      <div className="login-page__container">
        {/* Logo */}
        <div className="login-page__logo">
          <div className="login-page__logo-icon">
            <ShcLogo />
          </div>
          <h1 className="login-page__logo-text">{t("auth:login.title")}</h1>
        </div>

        <p className="login-page__subtitle">{t("auth:login.subtitle")}</p>

        {/* Form Card */}
        <LoginForm
          register={register}
          errors={errors}
          isSubmitting={loginMutation.isPending}
          isValid={isValid}
          isLocked={isLocked}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
        />

        {/* Links — below card */}
        <div className="login-page__links">
          <button
            type="button"
            onClick={handleShowIdHelp}
            className="login-page__link"
          >
            {t("auth:login.findId")}
          </button>
          <span className="login-page__footer-sep">|</span>
          <Link
            to="/reset-password"
            className={[
              "login-page__link",
              isLocked && "login-page__link--destructive",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {t("auth:login.forgotPassword")}
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="login-page__footer">
        {/* Organization details come from GET /api/v1/medical/institution (V129), not from the label
            catalog. Each row renders only when it has a value — no hardcoded fallback, because a
            fallback is the stale copy this change removes. The separator needs both sides present. */}
        {(institution.name || institution.address) && (
          <div className="login-page__footer-info">
            {institution.name && (
              <span>
                <strong>{institution.name}</strong>
              </span>
            )}
            {institution.name && institution.address && (
              <span className="login-page__footer-sep">|</span>
            )}
            {institution.address && <span>{institution.address}</span>}
          </div>
        )}
        <div className="login-page__footer-terms">
          {footerTerms.map((term, i) => (
            <Fragment key={term.typeId}>
              {i > 0 && <span className="login-page__footer-sep">|</span>}
              <Link to={`/terms-view?typeId=${term.typeId}`}>{term.name}</Link>
            </Fragment>
          ))}
        </div>      </footer>
    </main>
  );
};

export default MedicalLoginPage;
